import { Colorpicker, ColorpickerEvents, isHex, palette } from "@dhx/ts-colorpicker";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { create, el, awaitRedraw } from "@dhx/ts-common/dom";
import { isEmptyObj } from "@dhx/ts-common/core";
import { Label } from "./helper/label";
import { Popup, PopupEvents } from "@dhx/ts-popup";
import {
	ValidationStatus,
	IColorPickerConfig,
	ItemEvent,
	IColorPickerEventHandlersMap,
	IColorPicker,
	IColorpickerProps,
} from "../types";
import { getFormItemCss, getValidationMessage, isVerify, baseProps, widgetConfig } from "../helper";
import { IKeyManager, KeyManager } from "@dhx/ts-common/KeyManager";

export class ColorPicker extends Label implements IColorPicker {
	public config: IColorPickerConfig;
	public colorpicker: Colorpicker;
	public events: IEventSystem<ItemEvent, IColorPickerEventHandlersMap> = new EventSystem();

	private _keyManager: IKeyManager;
	private _popup: Popup;
	private _isValid = true;
	private _popupIsFocus = false;
	private _propsItem = [
		"required",
		"validation",
		"icon",
		"placeholder",
		"editable",
		"label",
		"labelWidth",
		"labelPosition",
		"hiddenLabel",
		"helpMessage",
		"preMessage",
		"successMessage",
		"errorMessage",
	];
	private _propsColorpicker = ["mode", "grayShades", "customColors", "palette"];
	private _props = [...baseProps, ...this._propsItem, ...this._propsColorpicker];

	constructor(container, config: IColorPickerConfig) {
		super(null, config);

		this._keyManager = new KeyManager(() => {
			const activeEl = document.activeElement;
			const element = this.getRootView()?.refs?.input?.el;

			return element === activeEl || this._popup.isVisible();
		});

		this._initView(config);
		this._initHandlers();
		this._initHotkeys();

		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	destructor() {
		this.events && this.events.clear();
		this._popup && this._popup.destructor();
		this.colorpicker && this.colorpicker.destructor();

		this.events = this._uid = this._propsColorpicker = this._propsItem = this._props = this._isValid = null;
		super._destructor();
		this.unmount();
	}
	setProperties(propertyConfig: IColorpickerProps): void {
		if (
			!propertyConfig ||
			isEmptyObj(propertyConfig) ||
			!this.events.fire(ItemEvent.beforeChangeProperties, [this.getProperties()])
		)
			return;

		for (const key in propertyConfig) {
			if (this._props.includes(key)) {
				this.config[key] = propertyConfig[key];
				if (this._propsColorpicker.includes(key)) {
					this.colorpicker.config[key] = propertyConfig[key];
				}
			}
		}
		this.events.fire(ItemEvent.afterChangeProperties, [this.getProperties()]);
		this.colorpicker.paint();
		this.paint();
	}
	getProperties(): IColorpickerProps {
		const obj: IColorpickerProps = {};
		for (const prop of this._props) {
			obj[prop] = this.config[prop];
		}
		return obj;
	}
	show(): void {
		const { value, hidden } = this.config;
		if (!hidden || !this.events.fire(ItemEvent.beforeShow, [value])) {
			return;
		}
		this.config.hidden = false;
		this.events.fire(ItemEvent.afterShow, [value]);
	}
	hide(init?: boolean): void {
		const { value, hidden } = this.config;
		if ((hidden && !init) || !this.events.fire(ItemEvent.beforeHide, [value, init])) {
			return;
		}
		this.config.hidden = true;
		this.events.fire(ItemEvent.afterHide, [value, init]);
	}
	isVisible(): boolean {
		return !this.config.hidden;
	}
	disable(): void {
		this.config.disabled = true;
		this.paint();
	}
	enable(): void {
		this.config.disabled = false;
		this.paint();
	}
	isDisabled(): boolean {
		return this.config.disabled;
	}
	validate(silent = false, validateValue?: string): boolean {
		const value = typeof validateValue === "undefined" ? this.getValue() : validateValue;

		if (!silent && !this.events.fire(ItemEvent.beforeValidate, [value])) return;
		this._isValid = this.config.validation ? this.config.validation(value) : isHex(value);
		!silent && this.events.fire(ItemEvent.afterValidate, [value, this._isValid]);

		return this._isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this.paint();
	}
	setValue(value: string): void {
		if (typeof value === "undefined" || value === this.config.value) return;
		this.colorpicker.setValue(value);
		isVerify(this.config) && this.validate();
	}
	getValue(): string {
		return this.config.value || "";
	}
	clear(): void {
		if (this.config.value === "") return;
		this.config.value = "";
		this.colorpicker.clear();
	}
	getWidget(): Colorpicker {
		return this.colorpicker;
	}
	focus(): void {
		awaitRedraw().then(() => {
			this.getRootView().refs.input.el.focus();
		});
	}
	blur(): void {
		awaitRedraw().then(() => {
			this._popup.hide();
			this.getRootView().refs.input.el.blur();
		});
	}
	protected _initView(config: IColorPickerConfig): void {
		if (isEmptyObj(config)) {
			throw new Error("Check the configuration is correct");
		}
		if (this.colorpicker) {
			this.colorpicker.destructor();
		}
		if (this._popup) {
			this._popup.destructor();
		}
		this.config = {
			type: config.type,
			id: config.id,
			name: config.name,
			disabled: false,
			editable: false,
			hidden: false,
			value: "",
			grayShades: true,
			pickerOnly: false,
			paletteOnly: false,
			customColors: [],
			palette,
			mode: "palette",
			required: false,
			icon: "",
			placeholder: "",
			label: "",
			labelWidth: "",
			labelPosition: "top",
			hiddenLabel: false,
			helpMessage: "",
			preMessage: "",
			successMessage: "",
			errorMessage: "",
			width: "content",
			height: "content",
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}

		this._popup = new Popup();
		this.colorpicker = new Colorpicker(null, widgetConfig(config));
		this._popup.attach(this.colorpicker);

		if (this.config.hidden) {
			awaitRedraw().then(() => {
				this.hide(true);
			});
		}
	}
	protected _initHandlers() {
		this.colorpicker.events.on(ColorpickerEvents.change, () => {
			const value = (this.config.value = this.colorpicker.getValue());
			this.events.fire(ItemEvent.change, [value]);
			this._popup.hide();
			this.paint();
		});
		this.events.on(ItemEvent.afterValidate, () => {
			this.config.$validationStatus = this._isValid ? ValidationStatus.success : ValidationStatus.error;
			this.paint();
		});
		this._popup.events.on(PopupEvents.afterHide, () => {
			if (document.activeElement !== this.getRootView().refs.input.el) {
				this.events.fire(ItemEvent.blur, [this.getValue()]);
			}
			this.paint();
		});
		this.events.on(ItemEvent.blur, () => {
			this._popupIsFocus = false;
			this.paint();
		});
		document.addEventListener("keydown", (event: KeyboardEvent) => {
			const activeEl = document.activeElement;
			const element = this.getRootView()?.refs?.input?.el;

			if (element === activeEl || this._popup.isVisible()) {
				this.events.fire(ItemEvent.keydown, [event]);
			}
		});
		document.addEventListener("mousedown", () => {
			const popup = this._popup.getContainer();
			const activeEl = document.activeElement;
			if (popup !== activeEl) this._popupIsFocus = false;
		});
	}
	protected _getHandlers() {
		return {
			onblur: () => {
				if (!this._popup.isVisible()) {
					this.events.fire(ItemEvent.blur, [this.getValue()]);
				}
				this.paint();
			},
			onfocus: () => {
				if (this._popup.isVisible()) {
					return;
				}
				this.config.value !== "" && this.clearValidate();
				const node = this.getRootView().refs.input.el;
				this._popup.show(node);
				this.events.fire(ItemEvent.focus, [this.getValue()]);
				this.paint();
			},
			oninput: (e: Event) => {
				const value = (e.target as HTMLInputElement).value;
				this.events.fire(ItemEvent.input, [value]);
			},
			onchange: (e: Event) => {
				const value = (e.target as HTMLInputElement).value;
				if (this.config.editable && isHex(value)) {
					this.setValue(value);
				} else if (value === "") {
					this.clear();
					isVerify(this.config) && this.validate();
					this.paint();
				} else {
					isVerify(this.config) && this.validate();
					this.paint();
				}
			},
		};
	}
	protected _initHotkeys() {
		const handlers = {
			"shift+tab": () => {
				this._applyTab();
			},
			tab: () => {
				this._applyTab();
			},
			escape: () => {
				if (this._popup.isVisible()) {
					this.getRootView().refs.input.el.focus();
				}
				this._popup.hide();
			},
			enter: (event: Event) => {
				event.preventDefault();
				if (!this._popup.isVisible()) {
					const node = this.getRootView().refs.input.el;
					this._popup.show(node);
				}
			},
			arrowRight: (event: Event) => {
				const focusElement = this._popup.getContainer();
				if (this._popup.isVisible() && !this._popupIsFocus) {
					event.preventDefault();
					focusElement.focus();
					this._popupIsFocus = true;
				}
			},
			onkeydown: (e: KeyboardEvent) => {
				if (e.key === "Tab") {
					this._popup.hide();
				}
			},
		};

		for (const key in handlers) {
			this._keyManager.addHotKey(key, handlers[key]);
		}
	}
	protected _draw() {
		const {
			required,
			value,
			icon,
			disabled,
			placeholder,
			name,
			id,
			editable,
			label,
			helpMessage,
		} = this.config;

		const activeFocus =
			this._popup.isVisible() || this.getRootView()?.refs?.input?.el === document.activeElement;

		return el(
			"div.dhx_form-group",
			{
				class: getFormItemCss(this.config, isVerify(this.config), activeFocus),
			},
			[
				this._drawLabel(),
				el(".dhx_input__wrapper", [
					el("div.dhx_input__container", {}, [
						el(".dhx_input__icon", {
							class:
								icon ||
								"dxi dxi-eyedropper-variant" +
									(value ? " dhx_input__icon--color-selected" : ""),
							style: { "background-color": value || "transparent" },
						}),
						el("input.dhx_input.dhx_input--icon-padding", {
							tabindex: 0,
							"data-dhx-id": name || id,
							_key: this._uid,
							_ref: "input",
							value,
							type: "text",
							required,
							disabled,
							class: activeFocus && "dhx_input--focus",
							placeholder: placeholder || "",
							name: name || "",
							id: id || this._uid,
							onfocus: this._handlers.onfocus,
							oninput: this._handlers.oninput,
							onchange: this._handlers.onchange,
							onblur: this._handlers.onblur,
							autocomplete: "off",
							readOnly: !editable,
							"aria-label": label || `${editable ? "type or" : ""} select color`,
							"aria-describedby": helpMessage ? `dhx_label__help_${id || this._uid}` : null,
						}),
					]),
					getValidationMessage(this.config) &&
						el("span.dhx_input__caption", {}, getValidationMessage(this.config)),
				]),
			]
		);
	}
	private _applyTab() {
		const activeEl = document.activeElement;
		const popup = this._popup.getRootView()?.refs?.content?.el;
		if (activeEl !== popup && !this._popupIsFocus) {
			this._popup.hide();
		}
	}
}
